import { useEffect, useRef, useState, useContext } from "react";
import AppNavigationContext from "@minohealth/common/services/AppNavigationContext";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import AnokyeList from "./components/anokye-list/AnokyeList";
import AnokyeGrid from "./components/anokye-grid/AnokyeGrid";
import { DocumentSharingWindow, ProjectSharingWindow } from "./components/anokye-sharing-window";
import useOnClickOutside from "@minohealth/common/hooks/useOnClickOutside";
import { showConfirm } from "@minohealth/common/utils";

import { FaChevronDown, FaPlus, FaArrowLeft } from "react-icons/fa";
import { IoIosSearch } from "react-icons/io";
import { LayoutGrid, List } from "lucide-react";
import { useSelector } from "react-redux";

import "./anokye-content.css";
import AnokyeApiService from "@minohealth/common/services/AnokyeApiService";
import { useChatContext } from "../../services/chat-provider";
import NewProjectModal from "../chat-sidebar/extras/NewProjectModal";

export default function AnokyeContent() {
  const { t } = useTranslation();
  const { appNavHistory } = useContext(AppNavigationContext);
  const { user } = useSelector((state) => state.app.datastate);

  const { useChatProjects, useCreateChatProject } = useChatContext()

  const { data: projects = [] } = useChatProjects()
  const [searchProjectQuery, setSearchProjectQuery] = useState("");

  const [viewOption, setViewOption] = useState(ViewOptions.LIST);
  const [sortByOption, setSortByOption] = useState(ContentSortType.LAST_UPDATED);
  const [orderOption, setOrderOption] = useState(DateOrder.NEWEST_FIRST);

  const { mutate: createNewProject } = useCreateChatProject()
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const projectSharingWindowRef = useRef(null);

  const handleShareProject = (project) => {
    projectSharingWindowRef.current?.openWindow(project?._id);
  };

  const handleDeleteProject = (id) => {
    showConfirm(
      "Are you sure you want to delete this project? This action cannot be undone.",
      "Delete Project",
      "critical"
    ).then(() => {
      // Optimistic UI update: Remove project from local state immediately
      // deleteProject(id);
    }).catch(() => { });
  };

  const selectedSortType = contentSortByOptions.find((option) => option.value === sortByOption);

  const filteredProjects = projects
    .filter((project) => project.title.toLowerCase().includes(searchProjectQuery.toLowerCase()))
    .sort((a, b) => {
      let startIndexA = a.title.toLowerCase().indexOf(searchProjectQuery.toLowerCase());
      if (startIndexA === -1) startIndexA = Infinity;
      let startIndexB = b.title.toLowerCase().indexOf(searchProjectQuery.toLowerCase());
      if (startIndexB === -1) startIndexB = Infinity;

      let diff = startIndexA - startIndexB;
      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    });

  const orderedFilteredProjects = filteredProjects.sort((a, b) => {
    switch (sortByOption) {
      case ContentSortType.ALPHABETICAL: {
        if (orderOption === AlphabeticalOrder.ASCENDING) {
          return a.title.localeCompare(b.title);
        }
        if (orderOption === AlphabeticalOrder.DESCENDING) {
          return b.title.localeCompare(a.title);
        }
        break;
      }
      case ContentSortType.DATE_CREATED: {
        if (orderOption === DateOrder.NEWEST_FIRST) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        if (orderOption === DateOrder.OLDEST_FIRST) {
          return new Date(a.createdAt) - new Date(b.createdAt);
        }
        break;
      }
      case ContentSortType.LAST_UPDATED: {
        if (orderOption === DateOrder.NEWEST_FIRST) {
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        }
        if (orderOption === DateOrder.OLDEST_FIRST) {
          return new Date(a.updatedAt) - new Date(b.updatedAt);
        }
        break;
      }
      default:
        throw new Error(`Invalid sort by option: ${sortByOption}`);
    }
  });

  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  useOnClickOutside(sortDropdownRef, () => setIsSortDropdownOpen(false));


  return (
    <div className="anokye__content_layout">
      <div className="anokye__title-row">
        <div className="flex items-center gap-3">
          <button
            onClick={() => appNavHistory.goBack()}
            className="flex items-center justify-center p-2 rounded-lg bg-transparent hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            title="Go back"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="anokye__page-title">Projects</h1>
        </div>
        <button
          type="button"
          className="anokye__new-project-btn"
          onClick={() => setIsProjectModalOpen(true)}
        >
          <FaPlus className="w-4 h-4" />
          <span>New project</span>
        </button>
      </div>

      {/* Row 2: Search Bar */}
      <div className="anokye__search-row">
        <form className="anokye__project-search-form">
          <div className="anokye__project-search-field">
            <IoIosSearch />
            <input
              type="search"
              placeholder={t("anokye.search-placeholder", "Search projects...")}
              className="anokye__project-search-input"
              value={searchProjectQuery}
              onChange={(ev) => setSearchProjectQuery(ev.target.value)}
            />
          </div>
        </form>
      </div>

      {/* Row 3: Filter/Sort dropdown and View Toggle */}
      <div className="anokye__sort-row">
        <div className="anokye__view-toggle">
          <button
            type="button"
            className={`anokye__view-btn ${viewOption === ViewOptions.GRID ? 'active' : ''}`}
            onClick={() => setViewOption(ViewOptions.GRID)}
            title="Grid view"
          >
            <LayoutGrid size={20} />
          </button>
          <button
            type="button"
            className={`anokye__view-btn ${viewOption === ViewOptions.LIST ? 'active' : ''}`}
            onClick={() => setViewOption(ViewOptions.LIST)}
            title="List view"
          >
            <List size={20} />
          </button>
        </div>

        <div className="anokye__sort-container" ref={sortDropdownRef}>
          <span className="anokye__sort-label">Sort by:</span>
          <div className="anokye__dropdown-wrapper">
            <button
              type="button"
              className="anokye__dropdown-trigger"
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
            >
              <span>{selectedSortType.label}</span>
              <FaChevronDown className={isSortDropdownOpen ? 'rotate-180' : ''} />
            </button>

            {isSortDropdownOpen && (
              <ul className="anokye__dropdown-menu">
                {contentSortByOptions.map((option) => (
                  <li
                    key={option.value}
                    className={`anokye__dropdown-item ${sortByOption === option.value ? 'active' : ''}`}
                    onClick={() => {
                      setSortByOption(option.value);
                      const defaultSortOrder = option.defaultOrder;
                      setOrderOption(defaultSortOrder);
                      setIsSortDropdownOpen(false);
                    }}
                  >
                    {option.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>


      <div className="anokye__content">
        {viewOption === ViewOptions.GRID && (
          <AnokyeGrid
            projects={orderedFilteredProjects}
            onDeleteProject={handleDeleteProject}
            onShareProject={handleShareProject}
          />
        )}
        {viewOption === ViewOptions.LIST && (
          <AnokyeList
            projects={orderedFilteredProjects}
            onDeleteProject={handleDeleteProject}
            onShareProject={handleShareProject}
          />
        )}
      </div>

      <DocumentSharingWindow document={{ name: "New Document.pdf" }} />
      <ProjectSharingWindow ref={projectSharingWindowRef} />

      <NewProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        onCreateProject={(title) => {
          createNewProject({ title });
        }}
      />
    </div >
  );
}

const ViewOptions = /** @type {const} */ ({
  GRID: "grid",
  LIST: "list",
});

const DateOrder = /** @type {const} */ ({
  NEWEST_FIRST: "newest-first",
  OLDEST_FIRST: "oldest-first",
});

const AlphabeticalOrder = /** @type {const} */ ({
  ASCENDING: "ascending",
  DESCENDING: "descending",
});

const ContentSortType = /** @type {const} */ ({
  ALPHABETICAL: "alphabetical",
  DATE_CREATED: "date-created",
  LAST_UPDATED: "last-updated",
});

const dateOrderOptions = [
  { label: "Newest First", value: DateOrder.NEWEST_FIRST },
  { label: "Oldest First", value: DateOrder.OLDEST_FIRST },
];

const alphabeticalOrderOptions = [
  { label: "A-Z", value: AlphabeticalOrder.ASCENDING },
  { label: "Z-A", value: AlphabeticalOrder.DESCENDING },
];

const contentSortByOptions = [
  {
    label: "Activity",
    value: ContentSortType.LAST_UPDATED,
    orderOptions: dateOrderOptions,
    defaultOrder: DateOrder.NEWEST_FIRST,
  },
  {
    label: "Date Created",
    value: ContentSortType.DATE_CREATED,
    orderOptions: dateOrderOptions,
    defaultOrder: DateOrder.NEWEST_FIRST,
  },
  {
    label: "Name",
    value: ContentSortType.ALPHABETICAL,
    orderOptions: alphabeticalOrderOptions,
    defaultOrder: AlphabeticalOrder.ASCENDING,
  },
];